import { forwardRef, Fragment, type ReactElement, useCallback, useEffect, useState } from 'react';
import { Button, VStack } from '@direct-frontend/components';
import { useDebouncedValue } from '@direct-frontend/stdlib';
import { useGeoCoderLocations } from '@/entities/geoCoder';
import { trackGoal } from '@/entities/trackGoal';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries, @direct-frontend/modules/entry-point -- Нужно поправить
import { type SelfPickUpAddressFieldValue } from '@/features/OrderForm/model/forms/fieldSelfPickUpAddress';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries, @direct-frontend/modules/entry-point -- Нужно поправить
import { PickupAddressSidebar } from '@/features/PickupAddressPicker//components/PickupAddressSiderbar';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries, @direct-frontend/modules/entry-point -- Нужно поправить
import { PickupAddressMap } from '@/features/PickupAddressPicker/components/PickupAddressMap';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries, @direct-frontend/modules/entry-point -- Нужно поправить
import { PickupAddressProvider } from '@/features/PickupAddressPicker/PickupAddressContext';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries, @direct-frontend/modules/entry-point -- Нужно поправить
import type { PickupAddressValue } from '@/features/PickupAddressPicker/types/PickupAddressValue';
import { type OrderDto } from '@/shared/api/order';
import { Dialog, DialogContent, DialogTitle } from '@/shared/components/Dialog';
import { buildLocation } from '@/shared/lib/buildLocation';
import { SelfPickupFilters } from './components/SelfPickupFilters';
import styles from './SelfPickUpPicker.module.css';

interface SelfPickUpSubmitButtonProps {
    isSubmitting: boolean;
    onSubmit: (callback?: VoidFunction) => void;
}

const SelfPickUpSubmitButton = forwardRef<HTMLElement, SelfPickUpSubmitButtonProps>(
    function SelfPickUpSubmitButton(props, forwardedRef) {
        const { isSubmitting, onSubmit } = props;

        return (
            <VStack ref={forwardedRef}>
                <Button size="m" color="accent" isDisabled={isSubmitting} onClick={onSubmit}>
                    Заберу отсюда
                </Button>
            </VStack>
        );
    }
);

interface PickupAddressPickerPayload {
    onOpenModal: VoidFunction;
}

export interface PickupPointPickerProps {
    order: OrderDto;
    reinitializeOrderForm: (initialOrder: OrderDto, updatedOrder: OrderDto) => void;
    storeSlug: string;
    defaultLocation: string;
    onChange?: (value: SelfPickUpAddressFieldValue) => void;
    children: (payload: PickupAddressPickerPayload) => ReactElement;
    value?: SelfPickUpAddressFieldValue;
}

export const SelfPickUpPicker = (props: PickupPointPickerProps) => {
    const { order, storeSlug, defaultLocation, children, onChange, value, reinitializeOrderForm } = props;
    const [searchQuery, setSearchQuery] = useState(value?.address ? buildLocation(value) : defaultLocation);
    const debounceQuery = useDebouncedValue(searchQuery, 300);
    const [isOpen, setIsOpen] = useState(false);
    const onOpenModal = useCallback(() => {
        trackGoal('pickup_popup_open');
        setIsOpen(true);
    }, [setIsOpen]);
    const onCloseModal = useCallback(() => setIsOpen(false), [setIsOpen]);
    const [loadGeoLocation, { data: locations = [] }] = useGeoCoderLocations();

    const handleChange = useCallback(
        (value: PickupAddressValue) => {
            setSearchQuery(value?.address ? buildLocation(value) : defaultLocation);
            onChange?.({
                pickUpWarehouseId: value.pickupPointId,
                address: value.address,
                locality: value.locality,
            });
            onCloseModal();
        },
        [defaultLocation, onChange, onCloseModal]
    );

    useEffect(() => {
        if (debounceQuery) {
            loadGeoLocation({ geocode: debounceQuery });
        }
    }, [loadGeoLocation, debounceQuery]);

    return (
        <Fragment>
            <PickupAddressProvider
                pickupId={value?.pickUpWarehouseId}
                storeSlug={storeSlug}
                orderId={order.id}
                type="self"
            >
                <Dialog
                    isFullscreen
                    containerClassName={styles.modalContainer}
                    className={styles.modal}
                    isOpen={isOpen}
                    onClose={onCloseModal}
                >
                    <DialogTitle title="Выберите магазин" />
                    <DialogContent className={styles.content}>
                        <PickupAddressSidebar
                            title="Выберите магазин"
                            filtersSlot={<SelfPickupFilters />}
                            order={order}
                            reinitializeOrderForm={reinitializeOrderForm}
                            storeSlug={storeSlug}
                            className={styles.sidebar}
                            inputValue={searchQuery}
                            showGeoInput={false}
                            onInputChange={setSearchQuery}
                            onChange={handleChange}
                            submitButton={SelfPickUpSubmitButton}
                        />
                        <div className={styles.mapWrapper}>
                            <div className={styles.map}>
                                <PickupAddressMap locations={locations} />
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </PickupAddressProvider>
            {children({ onOpenModal })}
        </Fragment>
    );
};
